import { useState } from 'react';
import {
  Container,
  Group,
  Title,
  Card,
  Badge,
  Text,
  Switch,
  Table,
  Stack,
  Skeleton,
  Checkbox,
  Alert,
  List,
  Anchor,
  Divider,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconMessage, IconInfoCircle } from '@tabler/icons-react';
import { useSMSConsent, useOptInSMS, useOptOutSMS, useSMSDeliveryLogs } from '@/hooks/usePortalApi';

const STATUS_COLORS: Record<string, string> = {
  delivered: 'green',
  failed: 'red',
  pending: 'yellow',
};

export default function SMSPreferences() {
  const { data: consent, isLoading: loadingConsent } = useSMSConsent();
  const { data: logs, isLoading: loadingLogs } = useSMSDeliveryLogs();
  const optIn = useOptInSMS();
  const optOut = useOptOutSMS();
  const [agreed, setAgreed] = useState(false);

  const handleToggle = async (checked: boolean) => {
    try {
      if (checked) {
        if (!agreed) {
          notifications.show({
            title: 'Consent required',
            message: 'Please review and acknowledge the SMS Terms and Privacy Policy before opting in.',
            color: 'yellow',
          });
          return;
        }
        const consentText = `I agree to receive recurring automated SMS messages from Standard Produce Company for order, delivery, and account notifications. Message frequency varies (typically 2-10 per week). Msg & data rates may apply. Reply HELP for help, STOP to cancel. I have read and accept the SMS Terms of Service and Privacy Policy. Acknowledged at ${new Date().toISOString()}.`;
        await optIn.mutateAsync({
          phone_number: consent?.phone_number || '',
          consent_method: 'web_form',
          consent_text: consentText,
        });
        notifications.show({
          title: 'Subscribed',
          message: 'SMS notifications enabled. You will receive a confirmation text shortly.',
          color: 'green',
        });
      } else {
        await optOut.mutateAsync();
        setAgreed(false);
        notifications.show({
          title: 'Unsubscribed',
          message: 'SMS notifications disabled. You will no longer receive messages.',
          color: 'blue',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update SMS preferences',
        color: 'red',
      });
    }
  };

  const isOptedIn = consent?.opted_in || false;

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Group>
          <IconMessage size={28} />
          <Title order={2}>SMS Notifications</Title>
        </Group>
      </Group>

      {loadingConsent ? (
        <Skeleton height={400} mb="xl" />
      ) : (
        <Card shadow="sm" padding="lg" mb="xl">
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text fw={600} size="lg">Current Status</Text>
                <Text size="sm" c="dimmed">Manage your SMS notification preferences</Text>
              </div>
              <Badge color={isOptedIn ? 'green' : 'red'} variant="light" size="lg">
                {isOptedIn ? 'Opted In' : 'Opted Out'}
              </Badge>
            </Group>

            {consent?.phone_number && (
              <Text size="sm">
                <Text component="span" fw={600}>Phone Number:</Text> {consent.phone_number}
              </Text>
            )}

            <Divider />

            <Stack gap="xs">
              <Text fw={600} size="sm">Program Details</Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  <Text component="span" fw={500}>Program:</Text> Standard Produce Customer Care SMS
                </List.Item>
                <List.Item>
                  <Text component="span" fw={500}>Message types:</Text> Order confirmations,
                  delivery ETAs, account balance / invoice alerts, and responses to SMS commands
                  (ORDER, BAL, PAY, HELP).
                </List.Item>
                <List.Item>
                  <Text component="span" fw={500}>Message frequency:</Text> Varies based on your
                  ordering activity — typically 2-10 messages per week for active accounts.
                </List.Item>
                <List.Item>
                  <Text component="span" fw={500}>Cost:</Text> Message and data rates may apply.
                  Check with your mobile carrier for details.
                </List.Item>
                <List.Item>
                  <Text component="span" fw={500}>Opt out:</Text> Reply <strong>STOP</strong> to
                  any message at any time. Reply <strong>HELP</strong> for help or call
                  (434) 204-2184.
                </List.Item>
                <List.Item>
                  <Text component="span" fw={500}>Privacy:</Text> Mobile numbers and SMS opt-in
                  data are never shared with third parties or affiliates for marketing or
                  promotional purposes.
                </List.Item>
              </List>
            </Stack>

            <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
              <Text size="sm" fw={600} mb={4}>Sample message:</Text>
              <Text size="sm" ff="monospace">
                Standard Produce: Your order #SO-1042 is confirmed — 10cs Romaine, 5cs Roma
                Tomatoes, 3cs Green Peppers. Total $487.50. Delivery tomorrow 6-8AM. Reply
                HELP for help, STOP to cancel. Msg&amp;data rates may apply.
              </Text>
            </Alert>

            {!isOptedIn && (
              <Checkbox
                checked={agreed}
                onChange={(e) => setAgreed(e.currentTarget.checked)}
                label={
                  <Text size="sm">
                    I agree to receive recurring automated SMS messages from Standard Produce
                    at the phone number on my account. I have read and accept the{' '}
                    <Anchor href="/sms-terms" target="_blank" inherit>SMS Terms of Service</Anchor>
                    {' '}and{' '}
                    <Anchor href="/privacy" target="_blank" inherit>Privacy Policy</Anchor>.
                    Consent is not a condition of purchase.
                  </Text>
                }
              />
            )}

            <Group justify="space-between">
              <Text fw={500}>Receive SMS Notifications</Text>
              <Switch
                checked={isOptedIn}
                onChange={(e) => handleToggle(e.currentTarget.checked)}
                disabled={optIn.isPending || optOut.isPending || (!isOptedIn && !agreed)}
              />
            </Group>
          </Stack>
        </Card>
      )}

      <Title order={3} mb="md">Recent SMS Messages</Title>
      {loadingLogs ? (
        <Stack gap="xs">
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </Stack>
      ) : logs && logs.length > 0 ? (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Date</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Message Preview</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {logs.map((log: any) => (
              <Table.Tr key={log.id}>
                <Table.Td>{new Date(log.sent_at).toLocaleString()}</Table.Td>
                <Table.Td>{log.message_type}</Table.Td>
                <Table.Td>
                  <Badge color={STATUS_COLORS[log.status]} variant="light">
                    {log.status}
                  </Badge>
                </Table.Td>
                <Table.Td>{log.message_preview}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : (
        <Text ta="center" c="dimmed" py="xl">No SMS messages found</Text>
      )}
    </Container>
  );
}
